import { useState, useMemo, useCallback } from 'react';
import { Ship, Anchor, AlertTriangle, LifeBuoy, Plus, Trash2, ArrowUp, ArrowDown, RotateCcw, Users } from 'lucide-react';
import { type AppState, type SpeedBoatFactor, type FactorType } from './types';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { useToolMode, useToolHostContext, type ToolItem } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import {
  SpeedBoatLive,
  type SpeedBoatFactorType,
} from '../../components/prioritization/session/liveExperience/liveTools/SpeedBoatLive';
import { SpeedBoatLiveSetup } from './components/SpeedBoatLiveSetup';
import { SpeedBoatLiveBoardView } from './components/SpeedBoatLiveBoardView';
import { SpeedBoatLiveResultsView } from './components/SpeedBoatLiveResultsView';
import { useSpeedBoatLiveSession } from '../../hooks/useSpeedBoatLiveSession';
import { updateSessionStatus, recordEvent } from '../../lib/collaborativeGameService';

type PlayMode = 'solo' | 'live';

function SpeedBoatContent(props: ToolHostProps) {
  const toolMode = useToolMode();
  const { isLiveSession, shouldAutoStart } = useLiveToolSession();
  const isAppMode = toolMode === 'app';
  const [engineRanking, setEngineRanking] = useState<string[]>([]);
  
  const [playMode, setPlayMode] = useState<PlayMode | null>(null);
  const [state, setState] = useState<AppState>('input');
  const [factors, setFactors] = useState<SpeedBoatFactor[]>([]);
  const [newFactorName, setNewFactorName] = useState('');
  const [selectedType, setSelectedType] = useState<FactorType>('engine');

  // Live session state
  const [liveSessionId, setLiveSessionId] = useState<string | null>(null);
  const [liveMyName, setLiveMyName] = useState('');
  const [liveIsHost, setLiveIsHost] = useState(false);
  const [liveState, setLiveState] = useState<'setup' | 'playing' | 'results'>('setup');

  // Track source IDs for imported items
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});

  // Handler for importing items from workspace (import as engines by default)
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newFactors: SpeedBoatFactor[] = toolItems.map((item, idx) => ({
      id: item.id,
      name: item.name,
      type: 'engine' as FactorType, // Import as engines by default
      rank: factors.filter(f => f.type === 'engine').length + idx + 1,
    }));
    
    // Track source IDs
    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach(item => {
      if (item.sourceId) {
        newSourceIdMap[item.id] = item.sourceId;
      }
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));
    
    setFactors(prev => {
      const merged = [...prev, ...newFactors];
      if (isLiveSession) {
        const engineIds = merged.filter(f => f.type === 'engine').map(f => f.id);
        setEngineRanking(engineIds);
        setState('boat');
      }
      return merged;
    });
  }, [factors, isLiveSession]);
  
  // Live session
  const liveGame = useSpeedBoatLiveSession(liveSessionId, liveMyName);

  const handleSessionJoined = useCallback((sessionId: string, myName: string, isHost: boolean) => {
    setLiveSessionId(sessionId);
    setLiveMyName(myName);
    setLiveIsHost(isHost);
    setLiveState('playing');
  }, []);

  const handleEndLiveSession = useCallback(async () => {
    if (!liveSessionId || !liveMyName) return;
    await updateSessionStatus(liveSessionId, 'ended');
    await recordEvent(liveSessionId, liveMyName, 'session_ended', {});
    setLiveState('results');
  }, [liveSessionId, liveMyName]);

  const handleResetLive = useCallback(() => {
    setPlayMode(null);
    setLiveSessionId(null);
    setLiveMyName('');
    setLiveIsHost(false);
    setLiveState('setup');
  }, []);

  const liveEngineCount = factors.filter(f => f.type === 'engine').length;
  const liveRankingReady =
    isLiveSession &&
    liveEngineCount > 0 &&
    engineRanking.length === liveEngineCount;

  const handleTypeChange = (id: string, type: SpeedBoatFactorType) => {
    setFactors(prev =>
      prev.map(f => (f.id === id ? { ...f, type: type as FactorType } : f))
    );
    if (type === 'engine') {
      setEngineRanking(prev => (prev.includes(id) ? prev : [...prev, id]));
    } else {
      setEngineRanking(prev => prev.filter(rid => rid !== id));
    }
  };

  // Get results for saving as session result (engines and preservers sorted by rank)
  const handleGetResults = useCallback((): ToolResultItem[] => {
    // Sort engines by rank first, then preservers
    const sortedEngines: SpeedBoatFactor[] =
      isLiveSession && engineRanking.length > 0
        ? engineRanking
            .map(id => factors.find(f => f.id === id))
            .filter((f): f is SpeedBoatFactor => f != null)
        : factors.filter(f => f.type === 'engine').sort((a, b) => a.rank - b.rank);
    const sortedFactors = [
      ...sortedEngines,
      ...factors.filter(f => f.type === 'preserver').sort((a, b) => a.rank - b.rank),
    ];
    
    return sortedFactors.map((factor, idx) => ({
      id: sourceIdMap[factor.id] || factor.id,
      name: factor.name,
      rank: idx + 1,
      category: factor.type,
      metadata: {
        factorType: factor.type,
        originalRank: factor.rank,
      }
    }));
  }, [factors, sourceIdMap, engineRanking, isLiveSession]);

  // Initialize boat with factors
  const handleStart = () => {
    if (factors.length === 0) {
      alert('Please add at least one factor to start.');
      return;
    }
    setState('boat');
  };

  // Add a new factor
  const handleAddFactor = () => {
    if (!newFactorName.trim()) return;

    const factorsOfType = factors.filter((f) => f.type === selectedType);
    const newFactor: SpeedBoatFactor = {
      id: `factor-${Date.now()}`,
      name: newFactorName.trim(),
      type: selectedType,
      rank: factorsOfType.length + 1,
    };

    setFactors([...factors, newFactor]);
    setNewFactorName('');
  };

  // Delete a factor
  const handleDeleteFactor = (id: string) => {
    const factor = factors.find((f) => f.id === id);
    if (!factor) return;

    setFactors((prev) => {
      const filtered = prev.filter((f) => f.id !== id);
      return filtered.map((f) => {
        if (f.type === factor.type && f.rank > factor.rank) {
          return { ...f, rank: f.rank - 1 };
        }
        return f;
      });
    });
  };

  // Change factor type
  const handleChangeType = (id: string, newType: FactorType) => {
    setFactors((prev) => {
      const factor = prev.find((f) => f.id === id);
      if (!factor) return prev;

      const oldTypeFactors = prev.filter((f) => f.type === factor.type && f.id !== id);
      const newTypeFactors = prev.filter((f) => f.type === newType);

      const updatedOldType = oldTypeFactors.map((f) => {
        if (f.rank > factor.rank) {
          return { ...f, rank: f.rank - 1 };
        }
        return f;
      });

      const updatedFactor: SpeedBoatFactor = {
        ...factor,
        type: newType,
        rank: newTypeFactors.length + 1,
      };

      return [...prev.filter((f) => f.id !== id), ...updatedOldType, updatedFactor];
    });
  };

  // Move factor up in rank
  const handleMoveUp = (id: string) => {
    setFactors((prev) => {
      const factor = prev.find((f) => f.id === id);
      if (!factor || factor.rank === 1) return prev;

      const otherFactor = prev.find(
        (f) => f.type === factor.type && f.rank === factor.rank - 1
      );
      if (!otherFactor) return prev;

      return prev.map((f) => {
        if (f.id === id) return { ...f, rank: factor.rank - 1 };
        if (f.id === otherFactor.id) return { ...f, rank: factor.rank };
        return f;
      });
    });
  };

  // Move factor down in rank
  const handleMoveDown = (id: string) => {
    setFactors((prev) => {
      const factor = prev.find((f) => f.id === id);
      if (!factor) return prev;

      const maxRank = Math.max(
        ...prev.filter((f) => f.type === factor.type).map((f) => f.rank)
      );
      if (factor.rank === maxRank) return prev;

      const otherFactor = prev.find(
        (f) => f.type === factor.type && f.rank === factor.rank + 1
      );
      if (!otherFactor) return prev;

      return prev.map((f) => {
        if (f.id === id) return { ...f, rank: factor.rank + 1 };
        if (f.id === otherFactor.id) return { ...f, rank: factor.rank };
        return f;
      });
    });
  };

  // Get factors by type, sorted by rank
  const getFactorsByType = (type: FactorType) => {
    return factors
      .filter((f) => f.type === type)
      .sort((a, b) => a.rank - b.rank);
  };

  const engines = useMemo(() => getFactorsByType('engine'), [factors]);
  const anchors = useMemo(() => getFactorsByType('anchor'), [factors]);
  const rocks = useMemo(() => getFactorsByType('rock'), [factors]);
  const preservers = useMemo(() => getFactorsByType('preserver'), [factors]);

  // Reset
  const handleReset = () => {
    setState('input');
    setFactors([]);
    setNewFactorName('');
    setSelectedType('engine');
    setSourceIdMap({});
  };

  const typeConfig = {
    engine: {
      label: 'Engines (Driving Factors)',
      icon: Ship,
      color: 'blue',
      description: 'Motive forces pushing the boat forward',
      bgColor: 'bg-[var(--slate-dim)]',
      borderColor: 'border-[var(--slate)]',
      textColor: 'text-[var(--text-sec)]',
      iconColor: 'text-[var(--text-ter)]',
    },
    anchor: {
      label: 'Anchors (Holding Back)',
      icon: Anchor,
      color: 'red',
      description: 'Factors slowing down progress',
      bgColor: 'bg-[var(--coral-dim)]',
      borderColor: 'border-[var(--coral)]',
      textColor: 'text-[var(--coral)]',
      iconColor: 'text-[var(--coral)]',
    },
    rock: {
      label: 'Rocks (Risks)',
      icon: AlertTriangle,
      color: 'orange',
      description: 'Risks to be avoided',
      bgColor: 'bg-[var(--mustard-dim)]',
      borderColor: 'border-[var(--mustard)]',
      textColor: 'text-[var(--mustard)]',
      iconColor: 'text-[var(--mustard)]',
    },
    preserver: {
      label: 'Life Preservers (Risk Mitigations)',
      icon: LifeBuoy,
      color: 'green',
      description: 'Risk mitigations and safeguards',
      bgColor: 'bg-[var(--green-dim)]',
      borderColor: 'border-[var(--green)]',
      textColor: 'text-[var(--green)] dark:text-[var(--text-ter)]',
      iconColor: 'text-[var(--green)]',
    },
  };

  const renderFactorCard = (factor: SpeedBoatFactor, type: FactorType) => {
    const config = typeConfig[type];
    const Icon = config.icon;
    const factorsOfType = getFactorsByType(type);
    const maxRank = factorsOfType.length;

    return (
      <div
        key={factor.id}
        className={`p-4  border-2 ${config.bgColor} ${config.borderColor} ${config.textColor}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-3 flex-1">
            <div className={`p-2 rounded ${config.bgColor} border ${config.borderColor}`}>
              <Icon className={`h-5 w-5 ${config.iconColor}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold">#{factor.rank}</span>
                <span>{factor.name}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleMoveUp(factor.id)}
              disabled={factor.rank === 1}
              className="p-1 hover:bg-[var(--bg-raised)]/50 rounded disabled:opacity-30"
              title="Move up"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleMoveDown(factor.id)}
              disabled={factor.rank === maxRank}
              className="p-1 hover:bg-[var(--bg-raised)]/50 rounded disabled:opacity-30"
              title="Move down"
            >
              <ArrowDown className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleDeleteFactor(factor.id)}
              className="p-1 hover:bg-[var(--bg-raised)]/50 rounded text-[var(--coral)]"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-current/20">
          <select
            value={factor.type}
            onChange={(e) => handleChangeType(factor.id, e.target.value as FactorType)}
            className={`text-xs px-2 py-1 rounded border ${config.borderColor} ${config.bgColor} ${config.textColor}`}
          >
            <option value="engine">Engine</option>
            <option value="anchor">Anchor</option>
            <option value="rock">Rock</option>
            <option value="preserver">Life Preserver</option>
          </select>
        </div>
      </div>
    );
  };

  return (
    <ToolWrapper
      toolName="Speed Boat"
      toolDescription="Identify factors driving or anchoring progress"
      onImportItems={handleImportItems}
      itemCount={factors.length}
      onGetResults={liveRankingReady || state === 'boat' ? handleGetResults : undefined}
      {...props}
    >
      {isLiveSession && state === 'boat' && factors.length > 0 && (
        <LiveToolScaffold
          title="Speed boat"
          subtitle="Classify factors and rank what moves you forward"
        >
          <SpeedBoatLive
            factors={factors.map(f => ({ id: f.id, name: f.name, type: f.type }))}
            engineRanking={engineRanking}
            onEngineRankingChange={setEngineRanking}
            onTypeChange={handleTypeChange}
          />
        </LiveToolScaffold>
      )}

      {/* ── Live session flows ─────────────────────────────────────────── */}
      {playMode === 'live' && liveState === 'setup' && (
        <div className="w-full max-w-2xl mx-auto p-4">
          <SpeedBoatLiveSetup
            onSessionJoined={handleSessionJoined}
            onBack={() => setPlayMode(null)}
          />
        </div>
      )}

      {playMode === 'live' && liveState === 'playing' && liveSessionId && (
        <div className="flex flex-col h-[calc(100vh-120px)] min-h-[500px]">
          <SpeedBoatLiveBoardView
            game={liveGame}
            myName={liveMyName}
            isHost={liveIsHost}
            onEndSession={handleEndLiveSession}
          />
        </div>
      )}

      {playMode === 'live' && liveState === 'results' && (
        <div className="p-4">
          <SpeedBoatLiveResultsView
            game={liveGame}
            onReset={handleResetLive}
          />
        </div>
      )}

      {/* ── Solo flow ──────────────────────────────────────────────────── */}
      {playMode !== 'live' && !isLiveSession && (
      <div className="w-full max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 pb-4 border-b border-[var(--rule)]">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Ship className="h-8 w-8 text-[var(--text-ter)] flex-shrink-0" />
              <div>
                <h1 className="text-2xl font-bold text-[var(--text-pri)]">Speed Boat</h1>
                <p className="text-sm text-[var(--text-sec)] mt-1">
                  Identify factors driving or anchoring your team's progress toward goals.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {state === 'input' && (
                <button
                  onClick={() => setPlayMode('live')}
                  className="text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)] px-3 py-1.5 border border-[var(--slate)] hover:bg-[var(--slate-dim)] whitespace-nowrap flex items-center gap-2 transition-colors"
                >
                  <Users className="h-4 w-4" />
                  Live session
                </button>
              )}
              {state !== 'input' && (
                <button
                  onClick={handleReset}
                  className="text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] px-3 py-1 rounded hover:bg-[var(--hover-tint)] whitespace-nowrap flex items-center gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div>
          {state === 'input' && (
            <div className="space-y-6">
              {/* Show imported items if any */}
              {factors.length > 0 && factors.some(f => sourceIdMap[f.id]) && (
                <div className="p-4 bg-[var(--green-dim)] border border-[var(--green)]">
                  <h3 className="font-medium text-[var(--green)] dark:text-[var(--text-ter)] mb-2">
                    {factors.filter(f => sourceIdMap[f.id]).length} Items Imported as Engines
                  </h3>
                  <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mb-2">
                    You can change their type (Engine, Anchor, Rock, Life Preserver) after launching.
                  </p>
                </div>
              )}
              
              <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
                <h3 className="font-semibold text-[var(--text-sec)] mb-2">How to Use</h3>
                <ul className="text-sm text-[var(--text-sec)] space-y-1 list-disc list-inside">
                  <li>
                    <strong>Engines:</strong> Motive forces pushing the boat forward (driving factors)
                  </li>
                  <li>
                    <strong>Anchors:</strong> Factors holding the boat back (obstacles)
                  </li>
                  <li>
                    <strong>Rocks:</strong> Risks to be avoided
                  </li>
                  <li>
                    <strong>Life Preservers:</strong> Risk mitigations and safeguards
                  </li>
                  <li className="mt-2">Add factors and categorize them. Stack rank items within each category.</li>
                </ul>
              </div>

              <div className="space-y-4">
                <div className="flex gap-2">
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value as FactorType)}
                    className="px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="engine">Engine (Driving Factor)</option>
                    <option value="anchor">Anchor (Holding Back)</option>
                    <option value="rock">Rock (Risk)</option>
                    <option value="preserver">Life Preserver (Risk Mitigation)</option>
                  </select>
                  <input
                    type="text"
                    value={newFactorName}
                    onChange={(e) => setNewFactorName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddFactor()}
                    placeholder="Enter a factor..."
                    className="flex-1 px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleAddFactor}
                    className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </button>
                </div>

                {factors.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[var(--text-pri)]">Factors ({factors.length})</h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      {factors.map((factor) => {
                        const config = typeConfig[factor.type];
                        const Icon = config.icon;
                        return (
                          <div
                            key={factor.id}
                            className="flex items-center justify-between p-3 bg-[var(--bg-raised)] border border-[var(--rule)]"
                          >
                            <div className="flex items-center gap-2">
                              <Icon className={`h-4 w-4 ${config.iconColor}`} />
                              <span className="text-[var(--text-pri)]">{factor.name}</span>
                              <span className="text-xs text-[var(--text-ter)]">({config.label.split(' ')[0]})</span>
                            </div>
                            <button
                              onClick={() => handleDeleteFactor(factor.id)}
                              className="text-[var(--coral)] hover:text-[var(--coral)] p-1"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleStart}
                  disabled={factors.length === 0}
                  className="w-full px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed font-semibold"
                >
                  Launch Speed Boat
                </button>
              </div>
            </div>
          )}

          {state === 'boat' && (
            <div className="space-y-6">
              {/* Boat Visualization */}
              <div className="bg-gradient-to-b from-blue-50 to-white border-2 border-[var(--slate)] p-8 min-h-[500px]">
                <div className="flex items-center justify-center mb-8">
                  <div className="text-center">
                    <Ship className="h-20 w-20 text-[var(--text-ter)] mx-auto mb-2" />
                    <h2 className="text-xl font-bold text-[var(--text-pri)]">Speed Boat</h2>
                    <p className="text-sm text-[var(--text-sec)]">Identify what's driving or anchoring your progress</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Engines */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Ship className="h-5 w-5 text-[var(--text-ter)]" />
                      <h3 className="font-semibold text-[var(--text-pri)]">
                        {typeConfig.engine.label} ({engines.length})
                      </h3>
                    </div>
                    <p className="text-xs text-[var(--text-sec)] mb-3">{typeConfig.engine.description}</p>
                    {engines.length === 0 ? (
                      <p className="text-[var(--text-ter)] text-sm italic">No engines added</p>
                    ) : (
                      <div className="space-y-2">
                        {engines.map((factor) => renderFactorCard(factor, 'engine'))}
                      </div>
                    )}
                  </div>

                  {/* Anchors */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Anchor className="h-5 w-5 text-[var(--coral)]" />
                      <h3 className="font-semibold text-[var(--text-pri)]">
                        {typeConfig.anchor.label} ({anchors.length})
                      </h3>
                    </div>
                    <p className="text-xs text-[var(--text-sec)] mb-3">{typeConfig.anchor.description}</p>
                    {anchors.length === 0 ? (
                      <p className="text-[var(--text-ter)] text-sm italic">No anchors added</p>
                    ) : (
                      <div className="space-y-2">
                        {anchors.map((factor) => renderFactorCard(factor, 'anchor'))}
                      </div>
                    )}
                  </div>

                  {/* Rocks */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-5 w-5 text-[var(--mustard)]" />
                      <h3 className="font-semibold text-[var(--text-pri)]">
                        {typeConfig.rock.label} ({rocks.length})
                      </h3>
                    </div>
                    <p className="text-xs text-[var(--text-sec)] mb-3">{typeConfig.rock.description}</p>
                    {rocks.length === 0 ? (
                      <p className="text-[var(--text-ter)] text-sm italic">No rocks added</p>
                    ) : (
                      <div className="space-y-2">
                        {rocks.map((factor) => renderFactorCard(factor, 'rock'))}
                      </div>
                    )}
                  </div>

                  {/* Life Preservers */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-2">
                      <LifeBuoy className="h-5 w-5 text-[var(--green)]" />
                      <h3 className="font-semibold text-[var(--text-pri)]">
                        {typeConfig.preserver.label} ({preservers.length})
                      </h3>
                    </div>
                    <p className="text-xs text-[var(--text-sec)] mb-3">{typeConfig.preserver.description}</p>
                    {preservers.length === 0 ? (
                      <p className="text-[var(--text-ter)] text-sm italic">No life preservers added</p>
                    ) : (
                      <div className="space-y-2">
                        {preservers.map((factor) => renderFactorCard(factor, 'preserver'))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
                <h3 className="font-semibold text-[var(--text-pri)] mb-4">Summary</h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-[var(--slate-dim)]">
                    <div className="text-2xl font-bold text-[var(--text-sec)]">{engines.length}</div>
                    <div className="text-sm text-[var(--text-sec)]">Engines</div>
                  </div>
                  <div className="text-center p-4 bg-[var(--coral-dim)]">
                    <div className="text-2xl font-bold text-[var(--coral)]">{anchors.length}</div>
                    <div className="text-sm text-[var(--text-sec)]">Anchors</div>
                  </div>
                  <div className="text-center p-4 bg-[var(--mustard-dim)]">
                    <div className="text-2xl font-bold text-[var(--mustard)]">{rocks.length}</div>
                    <div className="text-sm text-[var(--text-sec)]">Rocks</div>
                  </div>
                  <div className="text-center p-4 bg-[var(--green-dim)]">
                    <div className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{preservers.length}</div>
                    <div className="text-sm text-[var(--text-sec)]">Life Preservers</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      )} {/* end solo flow */}
    </ToolWrapper>
  );
}

export default function SpeedBoat(props: ToolHostProps) {
  return <SpeedBoatContent {...props} />;
}
